import { useState, useEffect } from 'react'
import { useManagedDisksCalc } from '../../hooks/useCalculate'
import { useAnalytics } from '../../hooks/useAnalytics'
import type { ManagedDisksRequest } from '../../api/types'
import { TrustBar } from '../shared/TrustBar'
import { ShareButton } from '../shared/ShareButton'
import { FieldTooltip } from '../shared/FieldTooltip'
import { ScenarioPresets } from '../shared/ScenarioPresets'

type DiskType = ManagedDisksRequest['diskType']

const DISK_TYPE_OPTIONS: { value: DiskType; label: string; description: string }[] = [
  { value: 'standard_hdd',   label: 'Standard HDD',     description: 'Lowest cost. Transaction charges apply — no cap.' },
  { value: 'standard_ssd',   label: 'Standard SSD',     description: 'Balanced. Transaction charges apply.' },
  { value: 'premium_ssd',    label: 'Premium SSD',      description: 'Fixed price, no transactions. Best for production.' },
  { value: 'premium_ssd_v2', label: 'Premium SSD v2',   description: '3D pricing: capacity + IOPS + throughput.' },
  { value: 'ultra',          label: 'Ultra Disk',       description: 'Maximum performance. 3D pricing.' },
]

const HDD_SKUS = [
  { value: 'S4',  label: 'S4  — 32 GB' },
  { value: 'S6',  label: 'S6  — 64 GB' },
  { value: 'S10', label: 'S10 — 128 GB' },
  { value: 'S15', label: 'S15 — 256 GB' },
  { value: 'S20', label: 'S20 — 512 GB' },
  { value: 'S30', label: 'S30 — 1 TB' },
  { value: 'S40', label: 'S40 — 2 TB' },
  { value: 'S50', label: 'S50 — 4 TB' },
  { value: 'S60', label: 'S60 — 8 TB' },
  { value: 'S70', label: 'S70 — 16 TB' },
  { value: 'S80', label: 'S80 — 32 TB' },
]

const SSD_SKUS = [
  { value: 'E1',  label: 'E1  — 4 GB' },
  { value: 'E2',  label: 'E2  — 8 GB' },
  { value: 'E3',  label: 'E3  — 16 GB' },
  { value: 'E4',  label: 'E4  — 32 GB' },
  { value: 'E6',  label: 'E6  — 64 GB' },
  { value: 'E10', label: 'E10 — 128 GB' },
  { value: 'E15', label: 'E15 — 256 GB' },
  { value: 'E20', label: 'E20 — 512 GB' },
  { value: 'E30', label: 'E30 — 1 TB' },
  { value: 'E40', label: 'E40 — 2 TB' },
  { value: 'E50', label: 'E50 — 4 TB' },
  { value: 'E60', label: 'E60 — 8 TB' },
  { value: 'E70', label: 'E70 — 16 TB' },
  { value: 'E80', label: 'E80 — 32 TB' },
]

const PREMIUM_SKUS = [
  { value: 'P1',  label: 'P1  — 4 GB' },
  { value: 'P2',  label: 'P2  — 8 GB' },
  { value: 'P3',  label: 'P3  — 16 GB' },
  { value: 'P4',  label: 'P4  — 32 GB' },
  { value: 'P6',  label: 'P6  — 64 GB' },
  { value: 'P10', label: 'P10 — 128 GB' },
  { value: 'P15', label: 'P15 — 256 GB' },
  { value: 'P20', label: 'P20 — 512 GB' },
  { value: 'P30', label: 'P30 — 1 TB' },
  { value: 'P40', label: 'P40 — 2 TB' },
  { value: 'P50', label: 'P50 — 4 TB' },
  { value: 'P60', label: 'P60 — 8 TB' },
  { value: 'P70', label: 'P70 — 16 TB' },
  { value: 'P80', label: 'P80 — 32 TB' },
]

function fmt(n: number): string {
  return n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function InputRow({ label, children, tooltipContent }: { label: string; children: React.ReactNode; tooltipContent?: string }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[13px] font-medium" style={{ color: 'var(--text-secondary)' }}>{label}{tooltipContent && <FieldTooltip content={tooltipContent} />}</label>
      {children}
    </div>
  )
}

function inputStyle(): React.CSSProperties {
  return {
    backgroundColor: 'var(--surface-container)',
    border: '1px solid var(--outline-variant)',
    color: 'var(--text-primary)',
    borderRadius: '6px',
    padding: '7px 10px',
    fontSize: '13px',
    width: '100%',
  }
}

export default function ManagedDisksCalc() {
  const [diskType, setDiskType] = useState<DiskType>('standard_hdd')
  const [sku, setSku]           = useState('S10')
  const [capacityGB, setCapacityGB]         = useState(128)
  const [provisionedIOPS, setProvisionedIOPS] = useState(3000)
  const [provisionedMBps, setProvisionedMBps] = useState(125)
  const [transactionsPerMonth, setTransactionsPerMonth] = useState(0)
  const [diskCount, setDiskCount] = useState(1)

  const inputs: ManagedDisksRequest = {
    diskType,
    sku: ['standard_hdd', 'standard_ssd', 'premium_ssd'].includes(diskType) ? sku : undefined,
    capacityGB,
    provisionedIOPS,
    provisionedMBps,
    transactionsPerMonth,
    diskCount,
    redundancy: 'lrs',
  }

  const { data, isLoading } = useManagedDisksCalc(inputs)
  const { trackCalculatorUsed } = useAnalytics()
  useEffect(() => {
    if (data?.breakdown?.total) {
      trackCalculatorUsed({ calculator: 'managed-disks', result_gbp: data.breakdown.total, tier: diskType })
    }
  }, [data?.breakdown?.total])

  const isSkuBased = diskType === 'standard_hdd' || diskType === 'standard_ssd' || diskType === 'premium_ssd'
  const isDimensionBased = diskType === 'premium_ssd_v2' || diskType === 'ultra'
  const hasTransactions = diskType === 'standard_hdd' || diskType === 'standard_ssd'
  const isHdd = diskType === 'standard_hdd'

  function getSkuOptions() {
    if (diskType === 'standard_hdd') return HDD_SKUS
    if (diskType === 'standard_ssd') return SSD_SKUS
    return PREMIUM_SKUS
  }

  function handleDiskTypeChange(value: DiskType) {
    setDiskType(value)
    if (value === 'standard_hdd') setSku('S10')
    else if (value === 'standard_ssd') setSku('E10')
    else if (value === 'premium_ssd') setSku('P10')
  }

  const bd = data?.breakdown
  const cmp = data?.hddVsSsdComparison

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-8">
      {/* ── Left column: inputs ── */}
      <div className="flex flex-col gap-6">
        <ScenarioPresets presets={[
          {
            label: 'OS disk (Standard SSD)',
            description: 'Standard SSD E10 · 128 GB · 1 disk — default OS disk for most VMs',
            apply: () => { setDiskType('standard_ssd'); setSku('E10'); setDiskCount(1); setTransactionsPerMonth(0); },
          },
          {
            label: 'Production data disks (Premium)',
            description: 'Premium SSD P20 · 512 GB · 4 disks — common production database setup',
            apply: () => { setDiskType('premium_ssd'); setSku('P20'); setDiskCount(4); setTransactionsPerMonth(0); },
          },
          {
            label: 'Ultra disk (high IOPS)',
            description: 'Ultra · 1 TB · 2 disks — latency-sensitive workloads, significant cost premium',
            apply: () => { setDiskType('ultra'); setCapacityGB(1024); setDiskCount(2); },
          },
        ]} />

        {/* Disk type selector */}
        <div>
          <p className="text-[13px] font-medium mb-3" style={{ color: 'var(--text-secondary)' }}>Disk type</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {DISK_TYPE_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => handleDiskTypeChange(opt.value)}
                className="flex items-start gap-2 p-2.5 rounded text-left transition-colors"
                style={{
                  backgroundColor: diskType === opt.value ? 'var(--surface-container-high)' : 'var(--surface-container)',
                  border: `1px solid ${diskType === opt.value ? '#0078d4' : 'var(--outline-variant)'}`,
                }}
              >
                <span
                  className="material-symbols-outlined text-[18px] mt-[1px]"
                  style={{ color: diskType === opt.value ? '#0078d4' : 'var(--text-tertiary)', fontVariationSettings: "'FILL' 1" }}
                >
                  {opt.value === 'ultra' ? 'bolt' : 'storage'}
                </span>
                <div>
                  <p className="text-[13px] font-semibold" style={{ color: 'var(--text-primary)' }}>{opt.label}</p>
                  <p className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>{opt.description}</p>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Amber HDD warning */}
        {isHdd && (
          <div className="p-3 rounded" style={{ backgroundColor: 'rgba(232,176,0,0.08)', border: '1px solid rgba(232,176,0,0.35)' }}>
            <p className="text-[13px]" style={{ color: '#e8b000' }}>
              <strong>No transaction cap on Standard HDD.</strong> Unlike Standard SSD, HDD transaction charges have no monthly ceiling. At high IOPS workloads this can cost more than Standard SSD. Enter your estimated monthly transactions below to see the comparison.
            </p>
          </div>
        )}

        {/* SKU selector */}
        {isSkuBased && (
          <InputRow label="Disk size (SKU)">
            <select value={sku} onChange={e => setSku(e.target.value)} style={inputStyle()}>
              {getSkuOptions().map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </InputRow>
        )}

        {/* Dimension-based inputs */}
        {isDimensionBased && (
          <>
            <InputRow label="Capacity (GiB)">
              <input
                type="number" min={1} max={65536} value={capacityGB}
                onChange={e => setCapacityGB(Math.max(1, Number(e.target.value)))}
                style={inputStyle()}
              />
            </InputRow>
            <InputRow label={`Provisioned IOPS${diskType === 'premium_ssd_v2' ? ' (first 3,000 free)' : ''}`}>
              <input
                type="number" min={0} max={160000} value={provisionedIOPS}
                onChange={e => setProvisionedIOPS(Math.max(0, Number(e.target.value)))}
                style={inputStyle()}
              />
            </InputRow>
            <InputRow label={`Provisioned throughput MB/s${diskType === 'premium_ssd_v2' ? ' (first 125 free)' : ''}`}>
              <input
                type="number" min={0} max={10000} value={provisionedMBps}
                onChange={e => setProvisionedMBps(Math.max(0, Number(e.target.value)))}
                style={inputStyle()}
              />
            </InputRow>
          </>
        )}

        {/* Transaction input */}
        {hasTransactions && (
          <InputRow label="Estimated transactions per month">
            <input
              type="number" min={0} value={transactionsPerMonth}
              onChange={e => setTransactionsPerMonth(Math.max(0, Number(e.target.value)))}
              style={inputStyle()}
              placeholder="0"
            />
            <p className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
              Each I/O operation = 1 transaction. E.g. 10M ops = 10,000,000.
            </p>
          </InputRow>
        )}

        {/* Disk count */}
        <InputRow label="Number of disks">
          <input
            type="number" min={1} max={1000} value={diskCount}
            onChange={e => setDiskCount(Math.max(1, Number(e.target.value)))}
            style={inputStyle()}
          />
        </InputRow>

        {/* Pricing note for dimension-based */}
        {isDimensionBased && (
          <p className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
            {diskType === 'premium_ssd_v2'
              ? 'Premium SSD v2 IOPS and throughput rates are hardcoded (Azure Retail Prices API returns £0 for these meters). Verified Apr 2026 from Microsoft documentation.'
              : 'Ultra Disk IOPS rate is hardcoded (Azure Retail Prices API returns £0 for this meter). Verified Apr 2026 from Microsoft documentation.'}
          </p>
        )}

        <TrustBar />
      </div>

      {/* ── Right column: results ── */}
      <div className="flex flex-col gap-5">

        {/* Total cost card */}
        <div className="p-5 rounded-lg" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-[13px] font-bold uppercase tracking-widest" style={{ color: 'var(--text-tertiary)' }}>Monthly estimate (PAYG)</h2>
            <ShareButton inputs={inputs} result={data ?? null} service="managed-disks" />
          </div>

          {isLoading ? (
            <p className="text-[13px]" style={{ color: 'var(--text-tertiary)' }}>Calculating…</p>
          ) : bd ? (
            <>
              <p className="text-3xl font-bold font-mono mb-1" style={{ color: 'var(--text-primary)' }}>
                £{fmt(bd.allDisks)}
              </p>
              <p className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>
                {diskCount > 1 ? `${diskCount} × £${fmt(bd.perDisk)} per disk · ` : ''}UK South · GBP
              </p>

              {/* Cost breakdown */}
              <div className="mt-4 pt-4 flex flex-col gap-2" style={{ borderTop: '1px solid var(--outline-variant)' }}>
                <div className="flex justify-between text-[13px]">
                  <span style={{ color: 'var(--text-secondary)' }}>Base disk fee</span>
                  <span className="font-mono" style={{ color: 'var(--text-primary)' }}>£{fmt(bd.diskFee)}</span>
                </div>
                {hasTransactions && (
                  <div className="flex justify-between text-[13px]">
                    <span style={{ color: 'var(--text-secondary)' }}>
                      Transaction charges
                      {diskType === 'standard_hdd' ? ' (no cap)' : ''}
                    </span>
                    <span className="font-mono" style={{ color: 'var(--text-primary)' }}>£{fmt(bd.transactions)}</span>
                  </div>
                )}
                {isDimensionBased && diskType === 'premium_ssd_v2' && (
                  <p className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
                    Capacity: £{fmt(capacityGB * 0.073)} · Extra IOPS: £{fmt(Math.max(0, provisionedIOPS - 3000) * 0.00657)} · Extra throughput: £{fmt(Math.max(0, provisionedMBps - 125) * 0.2197)}
                  </p>
                )}
                {isDimensionBased && diskType === 'ultra' && (
                  <p className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
                    Capacity: £{fmt(capacityGB * 0.146)} · IOPS: £{fmt(provisionedIOPS * 0.0550)} · Throughput: £{fmt(provisionedMBps * 0.073)}
                  </p>
                )}
                <div className="flex justify-between text-[13px] font-semibold pt-1" style={{ borderTop: '1px solid var(--outline-variant)' }}>
                  <span style={{ color: 'var(--text-primary)' }}>Per disk total</span>
                  <span className="font-mono" style={{ color: 'var(--text-primary)' }}>£{fmt(bd.perDisk)}</span>
                </div>
              </div>
            </>
          ) : null}
        </div>

        {/* HDD vs SSD comparison */}
        {cmp && (
          <div className="p-5 rounded-lg" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
            <h2 className="text-[13px] font-bold uppercase tracking-widest mb-4" style={{ color: 'var(--text-tertiary)' }}>
              HDD vs SSD comparison
            </h2>

            <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--outline-variant)' }}>
                  <th className="text-left pb-2 font-medium" style={{ color: 'var(--text-tertiary)' }}></th>
                  <th className="text-right pb-2 font-medium" style={{ color: 'var(--text-tertiary)' }}>Standard HDD</th>
                  <th className="text-right pb-2 font-medium" style={{ color: 'var(--text-tertiary)' }}>Standard SSD</th>
                </tr>
              </thead>
              <tbody>
                <tr className="py-1">
                  <td className="py-1.5" style={{ color: 'var(--text-secondary)' }}>Total per disk/month</td>
                  <td className={`text-right font-mono py-1.5 ${cmp.cheaperOption === 'hdd' ? 'font-semibold' : ''}`} style={{ color: cmp.cheaperOption === 'hdd' ? '#107c10' : 'var(--text-primary)' }}>
                    £{fmt(cmp.hddTotal)}
                    {cmp.cheaperOption === 'hdd' && <span className="ml-1 text-[11px]">✓ cheaper</span>}
                  </td>
                  <td className={`text-right font-mono py-1.5 ${cmp.cheaperOption === 'ssd' ? 'font-semibold' : ''}`} style={{ color: cmp.cheaperOption === 'ssd' ? '#107c10' : 'var(--text-primary)' }}>
                    £{fmt(cmp.ssdTotal)}
                    {cmp.cheaperOption === 'ssd' && <span className="ml-1 text-[11px]">✓ cheaper</span>}
                  </td>
                </tr>
              </tbody>
            </table>
            </div>

            {cmp.breakEvenTransactionsPerMonth > 0 && (
              <p className="text-[12px] mt-3 pt-3" style={{ borderTop: '1px solid var(--outline-variant)', color: 'var(--text-tertiary)' }}>
                HDD base fee covered by transactions above{' '}
                <span className="font-mono font-semibold" style={{ color: 'var(--text-secondary)' }}>
                  {cmp.breakEvenTransactionsPerMonth.toLocaleString()}
                </span>{' '}
                ops/month — at that volume HDD total cost equals the SSD base fee alone.
              </p>
            )}

            <p className="text-[11px] mt-2" style={{ color: 'var(--text-tertiary)' }}>
              HDD: £0.0005/10K ops, no cap. SSD: £0.0015/10K ops. Premium SSD: no transaction charges at all.
            </p>
          </div>
        )}

        {/* Transaction warning */}
        {data?.transactionWarning && !cmp && (
          <div className="p-4 rounded" style={{ backgroundColor: 'rgba(232,176,0,0.08)', border: '1px solid rgba(232,176,0,0.35)' }}>
            <p className="text-[13px]" style={{ color: '#e8b000' }}>
              <strong>HDD transaction charges accumulate with no monthly cap.</strong> Consider Premium SSD for high-IOPS workloads — it has no transaction billing at all.
            </p>
          </div>
        )}

        {/* Formula disclosure */}
        <a href="https://azure.microsoft.com/en-gb/pricing/details/managed-disks/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-[10px] font-mono" style={{ color: 'var(--text-tertiary)' }}>
          <span className="material-symbols-outlined text-[13px]">open_in_new</span>
          Verify on official Azure pricing page
        </a>

        {bd && (
          <p className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
            <strong>Formula:</strong>{' '}
            {isSkuBased
              ? `Base fee £${fmt(bd.diskFee)}${hasTransactions && transactionsPerMonth > 0 ? ` + (${transactionsPerMonth.toLocaleString()} ÷ 10,000) × £${diskType === 'standard_hdd' ? '0.0005' : '0.0015'} = £${fmt(bd.total)}` : ''}`
              : diskType === 'premium_ssd_v2'
              ? `${capacityGB} GiB × £0.073 + ${Math.max(0, provisionedIOPS - 3000)} extra IOPS × £0.00657 + ${Math.max(0, provisionedMBps - 125)} extra MBps × £0.2197 = £${fmt(bd.total)}`
              : `${capacityGB} GiB × £0.146 + ${provisionedIOPS} IOPS × £0.0550 + ${provisionedMBps} MBps × £0.073 = £${fmt(bd.total)}`
            }
            {diskCount > 1 ? ` × ${diskCount} disks = £${fmt(bd.allDisks)}` : ''}
          </p>
        )}
      </div>
    </div>
  )
}
